import fs from 'fs';
import path from 'path';
import { fileURLToPath } from 'url';
import logger from './logger.js';
import Handlebars from 'handlebars';

// Get the directory name from the current file's URL
const __dirname = path.dirname(fileURLToPath(import.meta.url));

// Path to templates directory
const TEMPLATES_DIR = path.resolve(__dirname, '../templates');

/**
 * Read a template file from the templates directory
 * @param filename Template filename
 * @returns Template content as string
 */
export function readTemplate(filename: string): string {
  try {
    const templatePath = path.join(TEMPLATES_DIR, filename);
    return fs.readFileSync(templatePath, 'utf-8');
  } catch (error) {
    logger.error(`Error reading template ${filename}: ${error}`);
    throw new Error(`Failed to read template: ${filename}`);
  }
}

/**
 * Write content to a file
 * @param filePath Path to write the file
 * @param content Content to write
 */
export function writeFile(filePath: string, content: string): void {
  try {
    // Ensure directory exists
    const dir = path.dirname(filePath);
    if (!fs.existsSync(dir)) {
      fs.mkdirSync(dir, { recursive: true });
    }
    
    fs.writeFileSync(filePath, content, 'utf-8');
    logger.info(`File written successfully: ${filePath}`);
  } catch (error) {
    logger.error(`Error writing file ${filePath}: ${error}`);
    throw new Error(`Failed to write file: ${filePath}`);
  }
}

/**
 * Generate a unique filename for a test
 * @param prefix Prefix for the filename
 * @param extension File extension
 * @returns Unique filename
 */
export function generateUniqueFilename(prefix: string, extension: string = 'yml'): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  return `${prefix}-${timestamp}.${extension}`;
}

/**
 * Simple template engine to replace placeholders in templates
 * @param template Template string
 * @param data Data object with values to replace
 * @returns Processed template
 */
export function processTemplate(template: string, data: Record<string, any>): string {
  const compiled = Handlebars.compile(template);
  return compiled(data);
}
